import React from "react";
import Modal from "../../../components/common/Modal";
import ContentToggle from "../../../components/common/ContentToggle";

interface SearchProjectsHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * SearchProjectsHelpModal - Modal help dialog for the "Search Projects" feature.
 *
 * Displays frequently asked questions about the Search Projects page using
 * expandable ContentToggle sections. Opened from the Search UBT Projects page
 * help popup when the user clicks "additional page help".
 */
const SearchProjectsHelpModal: React.FC<SearchProjectsHelpModalProps> = ({
  isOpen,
  onClose,
}) => {
  const MODAL_STYLES = `
  #modal-manual-trig .modal-container {
    max-width: 100% !important;
    width: 100% !important;
    top: 10% !important;
  }
`;
  return (
    <Modal
      isOpen={isOpen}
      title="Search Projects Questions & Answers"
      onClose={onClose}
      onSecondary={onClose}
      clearBtn="Close"
      contentClassName="mb-4!"
    >
      <div className="py-2">
        <ContentToggle
          titleText="What's new in Search Projects page?"
          isFillOption
        >
          <p className="text-[14px]!">
            UBT Tracker now gives you access to project information from{" "}
            <strong>all</strong> regions and locations. This is a powerful
            feature that you can use to learn best practices and share content
            knowledge outside your region.
          </p>
          <p className="mt-3! text-[14px]!">
            Although you have cross-regional view rights, you need to search for
            projects in each region separately. There is no &ldquo;all
            regions&rdquo; value in any location field. You may not edit
            projects&rsquo; information from other regions.
          </p>
        </ContentToggle>

        <ContentToggle titleText="How do I search for projects?" isFillOption>
          <p className="text-[14px]!">
            The Search Projects Page offers different ways to search for
            projects based on location, multiple cost centers, focus area,
            metric, project results, and test of change ratings.
          </p>
          <p className="mt-3! text-[14px]!">
            Although you have cross-regional view rights, you need to search for
            projects in each region separately. There is no &ldquo;all
            regions&rdquo; value in any location field. You may not edit
            projects&rsquo; information from other regions.
          </p>
          <p className="mt-3! text-[14px]!">
            All values for the focus area and metric fields are customized to
            each region. Contact your administrator if you have any suggested
            changes to the dropdown lists.
          </p>
        </ContentToggle>

        <ContentToggle titleText="How do I sort my results?" isFillOption>
          <p className="text-[14px]!">
            Click on the column header to sort the data from your search result.
            For example, click on &ldquo;Results&rdquo; and then click the
            column a second time to sort by highest rated projects. An arrow is
            displayed in the column that has been used to sort the displayed
            data. A down arrow means sort values &ldquo;decrease&rdquo;.
          </p>
          <p className="mt-3! text-[14px]! font-semibold! underline!">
            All filters applied to your search on this page will be kept when
            you move to other pages, such as Select UBT.
          </p>
          <p className="mt-2! text-[14px]!">
            Example: If you only have searched for projects from Cost Center
            #9999, all teams listed in the Select UBT Page will be associated
            with this Cost Center. You can change the filter options at any time
            and click the Search button to update your results. Once
            you&rsquo;ve reset the filters in the Search Project Page, the
            filters in the Select Team Page will also be updated with the latest
            search criteria used.
          </p>
          <p className="mt-3! text-[14px]!">
            See Also:{" "}
            <a
              href="https://www.lmpartnership.org/tools/ubt-tracker-user-guide"
              target="_blank"
              rel="noopener noreferrer"
              className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
            >
              https://www.lmpartnership.org/tools/ubt-tracker-user-guide
            </a>
          </p>
        </ContentToggle>
      </div>
      <style>{MODAL_STYLES}</style>
    </Modal>
  );
};

export default SearchProjectsHelpModal;
